import { AccountCircle as AccountCircleOutlined } from "@styled-icons/material-outlined/AccountCircle";
import { AccountCircle } from "@styled-icons/material/AccountCircle";
import { LockOpen } from "@styled-icons/material/LockOpen";
import { Lock } from "@styled-icons/material-outlined/Lock";
import { Logout } from "@styled-icons/material/Logout";
import { CardMembership } from "@styled-icons/material/CardMembership";
import { Help as HelpIcon } from "@styled-icons/material/Help";
import { HelpOutline as HelpIconOutline } from "@styled-icons/material/HelpOutline";
import type { MenuListItem } from "@naf/teamscheme";
import type { Dispatch, SetStateAction } from "react";
import { useCustomer } from "../../../auth/CustomerContext";
import { loginWithRedirect, logout } from "../../../auth/auth0Client";
import BECOME_MEMBER_URL from "../../../membership/BecomeMemberNafnoExternalUrl";
import MITT_NAF_URL from "../../../membership/MittNafExternalUrl";
import { externalLinkEvent } from "../../../tracking/externalLinkEvent";
import { EVENTS } from "../../../tracking/EVENTS";

export interface OrderedMenuListItem extends MenuListItem {
  order: number;
}

export type MaybeOrderedMenuListItem = MenuListItem | OrderedMenuListItem;

function createBecomeMemberProps(): MaybeOrderedMenuListItem {
  const props: MenuListItem = {
    key: "BliMedlem",
    text: "Bli medlem",
    icon: {
      icon: <CardMembership size={24} />,
    },
    href: BECOME_MEMBER_URL,
    trackEvent: externalLinkEvent(BECOME_MEMBER_URL),
  };
  return props;
}

function createLoggedInHeaderProps(): MaybeOrderedMenuListItem {
  const props: MenuListItem = {
    key: "MittNAF",
    text: "Mitt NAF",
    icon: {
      icon: <AccountCircleOutlined size={24} />,
      hoverIcon: <AccountCircle size={24} />,
    },
    href: MITT_NAF_URL,
    trackEvent: externalLinkEvent(MITT_NAF_URL),
  };
  return props;
}

function createLoginHeaderProps(): MaybeOrderedMenuListItem {
  const props: MenuListItem = {
    key: "LoggInn",
    text: "Logg inn",
    icon: {
      icon: <Lock size={24} />,
      hoverIcon: <LockOpen size={24} />,
    },
    trackEvent: EVENTS.HEADER.LOG_IN,
    onClick: () => {
      loginWithRedirect({
        targetUrl: `${window.location.pathname}${window.location.search}`,
      });
    },
  };
  return props;
}

function createLogoutHeaderProps(): MaybeOrderedMenuListItem {
  const props: MaybeOrderedMenuListItem = {
    key: "LoggUt",
    text: "Logg ut",
    icon: {
      icon: <Logout size={24} />,
    },
    trackEvent: EVENTS.HEADER.LOG_OUT,
    onClick: () => logout(),
    order: 1,
  };
  return props;
}

export function useAuthHeaderElements() {
  const customer = useCustomer();
  if (customer.isLoading) {
    const loading: MaybeOrderedMenuListItem = {
      key: "HenterBruker",
      text: "Henter bruker",
      isLoading: true,
    };
    return [loading];
  }
  if (customer.result && customer.isLoggedIn) {
    const loggedIn = createLoggedInHeaderProps();
    const logout = createLogoutHeaderProps();
    if (!customer.result.hasMembership) {
      return [loggedIn, logout, createBecomeMemberProps()];
    }
    return [loggedIn, logout];
  }
  return [createLoginHeaderProps(), createBecomeMemberProps()];
}

export function createFaqHeaderProps(
  isOpen: boolean,
  setOpen: Dispatch<SetStateAction<boolean>>,
): MaybeOrderedMenuListItem {
  const props: MenuListItem = {
    key: "FAQ",
    text: "Vanlige spørsmål",
    icon: {
      icon: <HelpIconOutline size={24} />,
      hoverIcon: <HelpIcon size={24} />,
    },
    onClick: () => setOpen(!isOpen),
    trackEvent: EVENTS.HEADER.FAQ,
  };
  return props;
}

export function combineMenuItems(
  items: MaybeOrderedMenuListItem[],
): OrderedMenuListItem[] {
  const filtered = items.filter((e): e is MaybeOrderedMenuListItem => !!e);

  return filtered
    .map((x, index) => {
      if ("order" in x) {
        const { order, ...rest } = x;
        return { order: index + order * filtered.length, ...rest };
      }

      return { order: index, ...x };
    })
    .sort((a, b) => a.order - b.order);
}
